import React, { useState } from 'react';
import SectionCard from '../components/properties/controls/SectionCard';
import Button from '../components/properties/controls/Button';
import Toggle from '../components/properties/controls/Toggle';
import Row from '../components/properties/controls/Row';
import ImageEditPanel from './tools/ImageEditPanel';
import ImageCropPanel from './tools/ImageCropPanel';
import ImageEraserPanel from './tools/ImageEraserPanel';
import styles from './ImageToolsController.module.css';

const ImageToolsController = ({
  model,
  disabled,
  toolState,
  onOpenTool,
  onCloseTool,
  onApplyCrop,
  onCancelCrop,
  onResetCrop,
  onClearEraser,
}) => {
  const [notice, setNotice] = useState('');
  const flipX = model.mixed('flipX');
  const flipY = model.mixed('flipY');
  const bgStatus = model.mixed('bgRemoval.status');
  const tool = toolState?.tool;
  const isOpen = Boolean(toolState?.open);

  const handleFlipX = () => {
    const nextValue = flipX.kind === 'mixed' ? true : !flipX.value;
    model.applyToSelection({ flipX: nextValue }, 'immediate', 'flip-x');
  };

  const handleFlipY = () => {
    const nextValue = flipY.kind === 'mixed' ? true : !flipY.value;
    model.applyToSelection({ flipY: nextValue }, 'immediate', 'flip-y');
  };

  const handleBgRemoval = () => {
    setNotice('BG Remover coming soon.');
  };

  const handleOpenTool = (nextTool) => {
    if (disabled) return;
    setNotice('');
    if (onOpenTool) {
      onOpenTool(nextTool);
    }
  };

  const handleCloseTool = () => {
    setNotice('');
    if (onCloseTool) {
      onCloseTool();
    }
  };

  const renderToolPanel = () => {
    if (!tool || !isOpen) return null;
    const title =
      tool === 'edit' ? 'Edit image' : tool === 'crop' ? 'Crop' : 'Eraser';
    return (
      <div className={styles.toolPanel}>
        <div className={styles.toolHeader}>
          <span className={styles.toolTitle}>{title}</span>
          <button type="button" className={styles.toolClose} onClick={handleCloseTool}>
            X
          </button>
        </div>
        {tool === 'edit' && <ImageEditPanel model={model} disabled={disabled} />}
        {tool === 'crop' && (
          <ImageCropPanel
            onApply={onApplyCrop}
            onCancel={onCancelCrop}
            onReset={onResetCrop}
          />
        )}
        {tool === 'eraser' && (
          <ImageEraserPanel
            model={model}
            disabled={disabled}
            onClear={onClearEraser}
            onDone={handleCloseTool}
          />
        )}
      </div>
    );
  };

  return (
    <div className={styles.stack}>
      {!isOpen && (
        <SectionCard title="Image">
          <Row label="Edit">
            <Button onClick={() => handleOpenTool('edit')} disabled={disabled}>
              Edit
            </Button>
          </Row>
          <Row label="Crop">
            <Button onClick={() => handleOpenTool('crop')} disabled={disabled}>
              Crop
            </Button>
          </Row>
          <Row label="Flip">
            <div style={{ display: 'flex', gap: '8px' }}>
              <Toggle
                checked={Boolean(flipX.value)}
                indeterminate={flipX.kind === 'mixed'}
                onChange={handleFlipX}
                label="Horizontal"
                disabled={disabled}
              />
              <Toggle
                checked={Boolean(flipY.value)}
                indeterminate={flipY.kind === 'mixed'}
                onChange={handleFlipY}
                label="Vertical"
                disabled={disabled}
              />
            </div>
          </Row>
          <Row label="BG Remover">
            <Button onClick={handleBgRemoval} disabled={disabled}>
              {bgStatus.value === 'processing' ? 'Removing...' : 'BG Remover'}
            </Button>
          </Row>
          <Row label="Eraser">
            <Button onClick={() => handleOpenTool('eraser')} disabled={disabled}>
              Eraser
            </Button>
          </Row>
          {notice && <div className={styles.notice}>{notice}</div>}
          {bgStatus.value === 'failed' && (
            <div className={styles.noticeError}>Background removal failed.</div>
          )}
          {bgStatus.value === 'done' && (
            <div className={styles.noticeSuccess}>Background removed.</div>
          )}
        </SectionCard>
      )}
      {renderToolPanel()}
    </div>
  );
};

export default ImageToolsController;
